import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ConsentConfig, CookiePreferences } from '@/types/cookies';
import { bootstrapAuth, getAccessToken, subscribe } from '@/lib/authToken';
import { apiRequest } from '@/lib/queryClient';

// Local storage key for guest preferences (device-level; not sent to backend)
const LOCAL_GUEST_PREFERENCES_KEY = 'cookie_preferences';
// TTL in days (12 months)
const TTL_DAYS = 365;

function isOlderThanDays(iso?: string | null, days = TTL_DAYS): boolean {
  if (!iso) return true;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return true;
  const ms = Date.now() - t;
  return ms > days * 24 * 60 * 60 * 1000;
}

type ConsentState = {
  consent: CookiePreferences | null; // null -> no decision yet
  config: ConsentConfig | null;
  loading: boolean;
  error?: string;
  hasConsentCookie: boolean; // kept for compatibility, but not used for logic anymore
  draft?: { analytics: boolean; marketing: boolean; functional?: boolean } | null;
  statusShowBanner?: boolean; // computed locally now
  statusLabel?: string | null; // unused
  statusSource?: 'user' | 'device' | null; // source context
  statusReason?: string | null; // unused
  policyVersion?: string | null; // from config if provided
};

type ConsentContextValue = ConsentState & {
  acceptAll: () => Promise<void>;
  rejectNonEssential: () => Promise<void>;
  saveManaged: (prefs: Pick<CookiePreferences, 'analytics' | 'marketing' | 'functional'>) => Promise<void>;
  refresh: () => Promise<void>;
  shouldShowBanner: boolean;
  setDraft: (prefs: { analytics: boolean; marketing: boolean; functional?: boolean } | null) => void;
  prefillForSettings: () => Promise<CookiePreferences | null>;
};

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ConsentState>({ consent: null, config: null, loading: true, hasConsentCookie: false, draft: null, statusShowBanner: undefined, statusLabel: null, statusSource: null, statusReason: null, policyVersion: null });

  const inFlightRef = useRef<Promise<void> | null>(null);

  const load = useCallback(async () => {
    if (inFlightRef.current) return inFlightRef.current;

    const p = (async () => {
      setState(s => ({ ...s, loading: true, error: undefined }));
      try {
        // If we don't yet have an in-memory token, wait for the app bootstrap
        // (single-flight silent refresh) before deciding guest vs user consent.
        if (!getAccessToken()) {
          await bootstrapAuth();
        }

        const authed = !!getAccessToken();
      if (authed) {
        // Authorized user: fetch user-level preferences
        const res = await apiRequest('GET', '/api/cookies/preferences');
        const json: any = await res.json().catch(() => null);
        const prefs = json && typeof json === 'object' ? json : null;
        if (prefs && (typeof prefs.analytics === 'boolean' || typeof prefs.functional === 'boolean' || typeof prefs.marketing === 'boolean')) {
          const normalized: CookiePreferences = {
            necessary: true,
            analytics: !!prefs.analytics,
            marketing: !!prefs.marketing,
            functional: !!prefs.functional,
            timestamp: prefs.updatedAt || new Date().toISOString(),
          };
          const expired = isOlderThanDays(prefs.updatedAt);
          setState(s => ({ ...s, consent: normalized, statusShowBanner: expired, statusSource: 'user', loading: false }));
        } else {
          // No user row => show banner
          setState(s => ({ ...s, consent: null, statusShowBanner: true, statusSource: 'user', loading: false }));
        }
      } else {
        // Guest: use localStorage only
        let local: CookiePreferences | null = null;
        try {
          const raw = localStorage.getItem(LOCAL_GUEST_PREFERENCES_KEY);
          if (raw) {
            const p = JSON.parse(raw);
            if (p && typeof p === 'object') {
              local = {
                necessary: true,
                analytics: !!p.analytics,
                marketing: !!p.marketing,
                functional: !!p.functional,
                timestamp: p.timestamp || new Date().toISOString(),
              };
            }
          }
        } catch { /* ignore */ }
        const expired = !local || isOlderThanDays(local.timestamp);
        setState(s => ({ ...s, consent: local, statusShowBanner: expired, statusSource: 'device', loading: false }));
      }
      } catch (e: any) {
        setState(s => ({ ...s, loading: false, error: e?.message, statusShowBanner: true }));
      }
    // Skip remote banner config fetch; rely on local copy defaults to avoid 404 noise if endpoint doesn't exist.
    })().finally(() => {
      inFlightRef.current = null;
    });

    inFlightRef.current = p;
    return p;
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Re-evaluate consent whenever auth token changes (login/logout), enabling
  // server-based checks and guest->user promotion.
  useEffect(() => {
    const unsub = subscribe(() => {
      load();
    });
    return () => { unsub(); };
  }, [load]);

  // Keep internal state in sync if another part of the app updates consent and dispatches the event
  useEffect(() => {
    const onChanged = (e: Event) => {
      try {
        const ce = e as CustomEvent<CookiePreferences>;
        const prefs = ce.detail;
        setState(s => ({ ...s, consent: prefs, hasConsentCookie: true }));
      } catch {
        // ignore
      }
    };
    window.addEventListener('cookie-consent-changed', onChanged);
    return () => window.removeEventListener('cookie-consent-changed', onChanged);
  }, []);

  const acceptAll = useCallback(async () => {
    // Accept all categories explicitly (optimistic update first)
    const next: CookiePreferences = { necessary: true, analytics: true, marketing: true, functional: true, timestamp: new Date().toISOString() };
    const authed = !!getAccessToken();
    setState(s => ({ ...s, consent: next, statusShowBanner: false, statusSource: authed ? 'user' : 'device' }));
    try { window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: next })); } catch {}

    if (authed) {
      // Persist in background; don't block UI
      try { await apiRequest('POST', '/api/cookies/preferences', { analytics: true, marketing: true, functional: true }); } catch { /* ignore */ }
    } else {
      try { localStorage.setItem(LOCAL_GUEST_PREFERENCES_KEY, JSON.stringify(next)); } catch {}
    }
  }, []);

  const rejectNonEssential = useCallback(async () => {
    // Optimistic first
    const next: CookiePreferences = { necessary: true, analytics: false, marketing: false, functional: false, timestamp: new Date().toISOString() };
    const authed = !!getAccessToken();
    setState(s => ({ ...s, consent: next, statusShowBanner: false, statusSource: authed ? 'user' : 'device' }));
    try { window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: next })); } catch {}

    if (authed) {
      try { await apiRequest('POST', '/api/cookies/preferences', { analytics: false, marketing: false, functional: false }); } catch { /* ignore */ }
    } else {
      try { localStorage.setItem(LOCAL_GUEST_PREFERENCES_KEY, JSON.stringify(next)); } catch {}
    }
  }, []);

  const saveManaged = useCallback(async (prefs: Pick<CookiePreferences, 'analytics' | 'marketing' | 'functional'>) => {
    const merged: CookiePreferences = { necessary: true, analytics: !!prefs.analytics, marketing: !!prefs.marketing, functional: !!(prefs as any).functional, timestamp: new Date().toISOString() };
    const authed = !!getAccessToken();
    // Optimistic update first: apply immediately in UI and to Consent Mode
    setState(s => ({ ...s, consent: merged, statusShowBanner: false, statusSource: authed ? 'user' : 'device' }));
    try { window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: merged })); } catch {}

    if (authed) {
      // Persist in background; don't block caller/UI
      try { await apiRequest('PUT', '/api/cookies/preferences', { analytics: merged.analytics, marketing: merged.marketing, functional: merged.functional }); } catch { /* ignore */ }
    } else {
      try { localStorage.setItem(LOCAL_GUEST_PREFERENCES_KEY, JSON.stringify(merged)); } catch {}
    }
  }, []);

  // Banner display relies primarily on backend /status response. No extra expiry needed here.
  // No-op expiry function (backend handles expiry in /status)

  // When user logs in while having device consent and no user record, promote device consent to user
  // Auto-promotion removed: do NOT promote device consent to user. If user logs in without a user row, show banner again.
  useEffect(() => {
    // When auth state changes, load() already re-evaluates and shows banner if needed
  }, [state.loading]);

  // Ref to ensure promotion runs only once per login session
  // promoteOnceRef removed (no auto-promotion anymore)

  const value = useMemo<ConsentContextValue>(() => ({
    ...state,
    acceptAll,
    rejectNonEssential,
    saveManaged,
    refresh: load,
    // Show banner if local state says no consent or statusShowBanner indicates to show
    shouldShowBanner: !state.loading && (state.statusShowBanner ?? (state.consent == null)),
    setDraft: (prefs) => setState(s => ({ ...s, draft: prefs })),
    prefillForSettings: async () => {
      try {
        if (getAccessToken()) {
          const res = await apiRequest('GET', '/api/cookies/preferences');
          const prefs: any = await res.json().catch(() => null);
          if (prefs && (typeof prefs.analytics === 'boolean' || typeof prefs.marketing === 'boolean')) {
            return { necessary: true, analytics: !!prefs.analytics, marketing: !!prefs.marketing, functional: !!prefs.functional, timestamp: prefs.updatedAt || new Date().toISOString() };
          }
          return null;
        } else {
          const raw = localStorage.getItem(LOCAL_GUEST_PREFERENCES_KEY);
          if (raw) {
            const p = JSON.parse(raw);
            return { necessary: true, analytics: !!p.analytics, marketing: !!p.marketing, functional: !!p.functional, timestamp: p.timestamp || new Date().toISOString() };
          }
          return null;
        }
      } catch { return state.consent; }
    },
  }), [state, acceptAll, rejectNonEssential, saveManaged, load]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useCookieConsent must be used within CookieConsentProvider');
  return ctx;
}
